import { useState, useEffect } from "react";
import { Sidebar, PageHeader, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { usePaywall } from "@/components/PaywallContext";
import { 
  TrendingUp, 
  Calculator, 
  Calendar,
  Sparkles,
  ArrowRight,
  PiggyBank,
  Lock
} from "lucide-react";
import UpgradeModal, { TRIGGER_SOURCES } from "@/components/UpgradeModal";

export default function ScenariosPage() {
  const { user } = useAuth();
  const { showPaywall, isPremium: checkPremium } = usePaywall();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [extraPayment, setExtraPayment] = useState(50);
  const [selectedBill, setSelectedBill] = useState('all');
  const [results, setResults] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const currency = user?.currency || 'GBP';
  const isPremium = checkPremium();

  useEffect(() => {
    const fetchBills = async () => {
      try {
        const response = await axios.get(`${API}/bills?is_debt=true`, { withCredentials: true });
        setBills(response.data);
      } catch (error) {
        console.error('Error fetching bills:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBills();
  }, []);

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const response = await axios.post(`${API}/scenarios/simulate`, {
        extra_payment: extraPayment,
        target_bill_id: selectedBill !== 'all' ? selectedBill : null
      }, { withCredentials: true });
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to run simulation');
    } finally {
      setSimulating(false);
    }
  };

  const debtBills = bills.filter(b => b.is_debt);
  const totalDebt = debtBills.reduce((acc, b) => acc + (b.total_debt || 0), 0);

  return (
    <Sidebar>
      <PageHeader 
        title="Scenario Planner"
        subtitle="See how extra payments impact your debt payoff"
      />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
        </div>
      ) : debtBills.length === 0 ? (
        <div className="glass-card p-12 rounded-2xl text-center">
          <Calculator className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
          <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">No debts to simulate</h3>
          <p className="text-p2p-muted mb-6">Add debt bills to use the scenario planner</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Simulation Controls */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6 rounded-2xl sticky top-4">
              <h3 className="font-outfit text-lg font-semibold text-p2p-gray mb-6">Simulation Settings</h3>
              
              <div className="space-y-6">
                {/* Extra Payment Slider */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-p2p-muted">Extra Monthly Payment</Label>
                    <span className="font-mono text-lg font-bold text-p2p-gold">
                      {formatCurrency(extraPayment, currency)}
                    </span>
                  </div>
                  <Slider
                    value={[extraPayment]}
                    onValueChange={(val) => setExtraPayment(val[0])}
                    min={0}
                    max={500}
                    step={10}
                    className="w-full"
                    data-testid="extra-payment-slider"
                  />
                  <div className="flex justify-between text-xs text-p2p-muted">
                    <span>{formatCurrency(0, currency)}</span>
                    <span>{formatCurrency(500, currency)}</span>
                  </div>
                </div>

                {/* Quick Amount Buttons */}
                <div className="flex flex-wrap gap-2">
                  {[25, 50, 100, 200, 300].map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      className={`border-white/20 ${extraPayment === amount ? 'bg-p2p-gold text-p2p-dark' : 'text-p2p-muted hover:bg-white/5'}`}
                      onClick={() => setExtraPayment(amount)}
                      data-testid={`quick-amount-${amount}`}
                    >
                      {formatCurrency(amount, currency)}
                    </Button>
                  ))}
                </div>

                {/* Target Debt Selection */}
                <div className="space-y-2">
                  <Label className="text-p2p-muted">Apply to</Label>
                  <Select value={selectedBill} onValueChange={setSelectedBill}>
                    <SelectTrigger className="input-dark" data-testid="target-debt-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-p2p-teal border-white/10">
                      <SelectItem value="all" className="text-p2p-gray hover:bg-white/10">
                        All Debts
                      </SelectItem>
                      {debtBills.map(bill => (
                        <SelectItem key={bill.bill_id} value={bill.bill_id} className="text-p2p-gray hover:bg-white/10">
                          {bill.name} ({formatCurrency(bill.total_debt || 0, currency)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  className="w-full btn-gold"
                  onClick={runSimulation}
                  disabled={simulating}
                  data-testid="run-simulation-btn"
                >
                  {simulating ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-p2p-dark" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" /> Run Simulation
                    </>
                  )}
                </Button>
              </div>

              {/* Current Debt Summary */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="text-sm text-p2p-muted mb-3">Current Debt Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-p2p-muted text-sm">Total Debt</span>
                    <span className="font-mono font-medium text-p2p-error">{formatCurrency(totalDebt, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-p2p-muted text-sm">Number of Debts</span>
                    <span className="font-mono font-medium text-p2p-gray">{debtBills.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            {!results ? (
              <div className="glass-card p-12 rounded-2xl text-center">
                <TrendingUp className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
                <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">
                  Ready to simulate
                </h3>
                <p className="text-p2p-muted">
                  Adjust the extra payment amount and click &quot;Run Simulation&quot; to see how it affects your debt payoff timeline
                </p>
              </div>
            ) : results.scenarios.length === 0 ? (
              <div className="glass-card p-12 rounded-2xl text-center">
                <Calculator className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
                <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">
                  No simulation results
                </h3>
                <p className="text-p2p-muted">
                  Make sure your debts have total debt amounts set
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Card - Always visible */}
                <div className="glass-card p-6 rounded-2xl bg-gradient-to-r from-p2p-gold/10 to-transparent border border-p2p-gold/20">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-p2p-gold/20 flex items-center justify-center">
                      <PiggyBank className="w-6 h-6 text-p2p-gold" />
                    </div>
                    <div>
                      <h3 className="font-outfit text-lg font-semibold text-p2p-gray">
                        With {formatCurrency(extraPayment, currency)} extra per month
                      </h3>
                      <p className="text-p2p-muted text-sm">Here&apos;s how your debt payoff changes</p>
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-3 gap-4">
                    {/* Months Saved - Always visible */}
                    <div className="text-center p-4 rounded-xl bg-white/5">
                      <p className="text-3xl font-bold font-mono text-p2p-success">
                        {results.scenarios.reduce((acc, s) => acc + s.months_saved, 0).toFixed(0)}
                      </p>
                      <p className="text-sm text-p2p-muted">Months Saved</p>
                    </div>
                    
                    {/* Interest Saved - Locked for free users */}
                    <div className={`text-center p-4 rounded-xl ${isPremium ? 'bg-white/5' : 'bg-white/5 relative overflow-hidden'}`}>
                      {isPremium ? (
                        <>
                          <p className="text-3xl font-bold font-mono text-p2p-gold">
                            {formatCurrency(results.scenarios.reduce((acc, s) => acc + s.interest_saved, 0), currency)}
                          </p>
                          <p className="text-sm text-p2p-muted">Interest Saved</p>
                        </>
                      ) : (
                        <div className="relative">
                          <p className="text-3xl font-bold font-mono text-p2p-muted/30 blur-sm select-none">
                            £XXX
                          </p>
                          <p className="text-sm text-p2p-muted">Interest Saved</p>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-p2p-muted" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Yearly Extra - Always visible */}
                    <div className="text-center p-4 rounded-xl bg-white/5">
                      <p className="text-3xl font-bold font-mono text-p2p-gray">
                        {formatCurrency(extraPayment * 12, currency)}
                      </p>
                      <p className="text-sm text-p2p-muted">Yearly Extra</p>
                    </div>
                  </div>
                </div>

                {/* Premium Unlock CTA - Show for free users */}
                {!isPremium && (
                  <div 
                    className="glass-card p-6 rounded-2xl border-2 border-dashed border-p2p-gold/30 bg-gradient-to-r from-p2p-gold/5 to-transparent cursor-pointer hover:border-p2p-gold/50 transition-all"
                    onClick={() => setUpgradeModalOpen(true)}
                    data-testid="simulation-upgrade-cta"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-p2p-gold/20 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-p2p-gold" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-outfit font-semibold text-p2p-gray">
                          See how to become debt-free faster
                        </h3>
                        <p className="text-p2p-muted text-sm">
                          Unlock full results: exact interest savings, improved cash flow, and personalized roadmap
                        </p>
                      </div>
                      <Button className="btn-gold whitespace-nowrap">
                        Unlock full simulation
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Individual Debt Results */}
                {results.scenarios.map((scenario, idx) => (
                  <div 
                    key={scenario.bill_id}
                    className={`glass-card p-6 rounded-2xl ${!isPremium && idx > 0 ? 'opacity-50 blur-sm pointer-events-none' : ''}`}
                    data-testid={`scenario-result-${scenario.bill_id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-outfit font-semibold text-p2p-gray">{scenario.name}</h3>
                        <p className="text-sm text-p2p-muted">
                          Balance: {formatCurrency(scenario.total_debt, currency)}
                        </p>
                      </div>
                      {scenario.months_saved > 0 && (
                        <span className="px-3 py-1 rounded-full bg-p2p-success/20 text-p2p-success text-sm font-medium">
                          {scenario.months_saved.toFixed(1)} months faster
                        </span>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      {/* Current Plan */}
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-xs text-p2p-muted uppercase tracking-wide mb-2">Current Plan</p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-p2p-muted text-sm">Monthly Payment</span>
                            <span className="font-mono text-p2p-gray">{formatCurrency(scenario.current_payment, currency)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-p2p-muted text-sm">Time to Payoff</span>
                            <span className="font-mono text-p2p-gray">{scenario.months_to_payoff_current.toFixed(1)} months</span>
                          </div>
                          {scenario.payoff_date_current && (
                            <div className="flex justify-between">
                              <span className="text-p2p-muted text-sm">Debt Free By</span>
                              <span className="font-mono text-p2p-muted text-sm">
                                {new Date(scenario.payoff_date_current).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Accelerated Plan - Partially locked for free */}
                      <div className="p-4 rounded-xl bg-p2p-gold/10 border border-p2p-gold/20">
                        <p className="text-xs text-p2p-gold uppercase tracking-wide mb-2">Accelerated Plan</p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-p2p-muted text-sm">Monthly Payment</span>
                            <span className="font-mono text-p2p-gold font-medium">{formatCurrency(scenario.new_payment, currency)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-p2p-muted text-sm">Time to Payoff</span>
                            <span className="font-mono text-p2p-success font-medium">{scenario.months_to_payoff_accelerated.toFixed(1)} months</span>
                          </div>
                          {scenario.payoff_date_accelerated && (
                            <div className="flex justify-between">
                              <span className="text-p2p-muted text-sm">Debt Free By</span>
                              <span className="font-mono text-p2p-success text-sm font-medium">
                                {new Date(scenario.payoff_date_accelerated).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {scenario.interest_saved > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                        <span className="text-p2p-muted text-sm">Potential Interest Saved</span>
                        {isPremium ? (
                          <span className="font-mono font-bold text-p2p-success">{formatCurrency(scenario.interest_saved, currency)}</span>
                        ) : (
                          <span className="font-mono font-bold text-p2p-muted/30 blur-sm select-none">£XXX</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Upgrade Modal */}
                <UpgradeModal
                  isOpen={upgradeModalOpen}
                  onClose={() => setUpgradeModalOpen(false)}
                  triggerSource={TRIGGER_SOURCES.SIMULATION}
                  contextData={{ extra_payment: extraPayment }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Sidebar>
  );
}
